import { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { api } from '../../../services/api'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideUp = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;
`

const Modal = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  width: 600px;
  max-width: 90vw;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
  animation: ${slideUp} 0.25s ease-out;
`

const ModalHeader = styled.div<{ type?: string }>`
  background: ${({ type }) => type === 'proyecto' ? '#4CAF50' : '#2196F3'};
  color: white;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-radius: ${({ theme }) => theme.borderRadius.lg} ${({ theme }) => theme.borderRadius.lg} 0 0;
`

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ModalSubtitle = styled.div`
  font-size: 0.8rem;
  opacity: 0.9;
  margin-top: 4px;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  margin-left: 12px;
  &:hover { opacity: 0.8; }
`

const ModalBody = styled.div`
  padding: 1.25rem 1.5rem;
`

const Section = styled.div`
  margin-bottom: 1.25rem;
  &:last-child { margin-bottom: 0; }
`

const SectionTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`

const InfoItem = styled.div``

const InfoLabel = styled.div`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  margin-bottom: 2px;
`

const InfoValue = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`

const TechBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  font-size: 0.75rem;
  margin: 2px 4px 2px 0;
`

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  background: ${({ status }) => {
        switch (status?.toLowerCase()) {
            case 'ganada': return '#2E7D32'
            case 'perdida': case 'cancelada': return '#C62828'
            case 'en_curso': case 'en curso': return '#1565C0'
            case 'en_negociacion': case 'pendiente': return '#FF9800'
            case 'presentada': case 'prospecto': return '#7B1FA2'
            case 'en_preparacion': case 'en_revision': return '#0288D1'
            case 'planificacion': return '#0288D1'
            case 'completado': case 'finalizado': return '#2E7D32'
            case 'pausado': return '#FF9800'
            case 'mantenimiento': return '#757575'
            case 'incompleta': return '#FF5722'
            default: return '#9E9E9E'
        }
    }};
`

const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const TeamMemberRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #f5f5f5;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.85rem;
`

const LoadBadge = styled.span<{ load: number }>`
  font-weight: 700;
  font-size: 0.8rem;
  color: ${({ load }) => {
        if (load > 80) return '#F44336'
        if (load > 50) return '#FF9800'
        return '#4CAF50'
    }};
`

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`

const FooterButton = styled.button<{ primary?: boolean }>`
  padding: 0.5rem 1.25rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ theme, primary }) => primary ? 'transparent' : theme.colors.border};
  background: ${({ theme, primary }) => primary ? theme.colors.primary : 'white'};
  color: ${({ primary }) => primary ? 'white' : 'inherit'};
  transition: all 0.15s ease;
  &:hover { opacity: 0.85; }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

const EmptyText = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
  font-size: 0.8rem;
`

const formatStatusLabel = (status: string): string => {
    const map: Record<string, string> = {
        ganada: 'Ganada', perdida: 'Perdida', cancelada: 'Cancelada',
        en_curso: 'En Curso', 'en curso': 'En Curso',
        en_negociacion: 'En Negociacion', presentada: 'Presentada',
        en_preparacion: 'En Preparacion', en_revision: 'En Revision',
        borrador: 'Borrador', planificacion: 'Planificacion',
        completado: 'Completado', finalizado: 'Finalizado',
        pausado: 'Pausado', mantenimiento: 'Mantenimiento',
        incompleta: 'Incompleta',
    }
    return map[status?.toLowerCase()] || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '-'
}

const formatDate = (dateStr?: string | null): string => {
    if (!dateStr) return '-'
    try {
        return new Date(dateStr).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch { return '-' }
}

const formatAmount = (amount?: number | null): string => {
    if (!amount) return '-'
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M\u20AC`
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K\u20AC`
    return `${amount}\u20AC`
}

interface OpportunitySummaryModalProps {
    opportunityId: number
    onClose: () => void
    isAdmin: boolean
}

export const OpportunitySummaryModal: React.FC<OpportunitySummaryModalProps> = ({
    opportunityId,
    onClose,
    isAdmin,
}) => {
    const navigate = useNavigate()
    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState(true)
    const [summary, setSummary] = useState<string | null>(null)
    const [summaryLoading, setSummaryLoading] = useState(false)

    useEffect(() => {
        const load = async () => {
            try {
                const res = await api.opportunities.getById(opportunityId)
                setData(res.data)
            } catch (err) {
                console.error('Error loading opportunity:', err)
            }
            setLoading(false)

            // Also fetch LLM summary
            setSummaryLoading(true)
            try {
                const summaryRes = await api.opportunities.getSummary(opportunityId)
                let raw = summaryRes.data?.summary ?? null
                // If the backend accidentally returned the raw Gemini content-block
                // array (list of {type, text, extras, ...}), extract just the text.
                if (raw) {
                    if (Array.isArray(raw)) {
                        raw = raw
                            .filter((b: any) => b?.type === 'text' || typeof b === 'string')
                            .map((b: any) => (typeof b === 'string' ? b : b?.text ?? ''))
                            .join('')
                    } else if (typeof raw === 'string') {
                        // Detect stringified JSON array
                        const trimmed = raw.trim()
                        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                            try {
                                const parsed = JSON.parse(trimmed.replace(/'/g, '"'))
                                if (Array.isArray(parsed)) {
                                    raw = parsed
                                        .filter((b: any) => b?.type === 'text' || typeof b === 'string')
                                        .map((b: any) => (typeof b === 'string' ? b : b?.text ?? ''))
                                        .join('')
                                }
                            } catch { /* not JSON, keep as-is */ }
                        }
                    }
                }
                setSummary(typeof raw === 'string' && raw.trim() ? raw : null)
            } catch {
                setSummary(null)
            }
            setSummaryLoading(false)
        }
        load()
    }, [opportunityId])

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose()
    }

    const handleViewDetails = () => {
        navigate('/oportunidades', { state: { openOpportunityId: opportunityId } })
    }

    if (loading) {
        return (
            <Overlay onClick={handleOverlayClick}>
                <Modal>
                    <LoadingContainer>Cargando...</LoadingContainer>
                </Modal>
            </Overlay>
        )
    }

    if (!data) {
        return (
            <Overlay onClick={handleOverlayClick}>
                <Modal>
                    <LoadingContainer>Error al cargar la oportunidad</LoadingContainer>
                    <ModalFooter>
                        <FooterButton onClick={onClose}>Cerrar</FooterButton>
                    </ModalFooter>
                </Modal>
            </Overlay>
        )
    }

    const general = data.general || {}
    const oferta = data.oferta || {}
    const proyecto = data.proyecto || {}
    const technologies: string[] = general.tecnologias || []

    // Determine current phase
    const hasProyecto = proyecto.estado && proyecto.estado !== ''
    const phaseType = hasProyecto ? 'proyecto' : 'oferta'

    return (
        <Overlay onClick={handleOverlayClick}>
            <Modal>
                <ModalHeader type={phaseType}>
                    <HeaderContent>
                        <ModalTitle title={general.name}>
                            {phaseType === 'proyecto' ? '🚀' : '📋'} {general.name || 'Sin nombre'}
                        </ModalTitle>
                        <ModalSubtitle>
                            {general.codigo && <span>Cod: {general.codigo} | </span>}
                            {general.sector && <span>{general.sector}</span>}
                        </ModalSubtitle>
                    </HeaderContent>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </ModalHeader>

                <ModalBody>
                    {/* General info */}
                    <Section>
                        <SectionTitle>Informacion General</SectionTitle>
                        <InfoGrid>
                            <InfoItem>
                                <InfoLabel>Gestor / Cliente</InfoLabel>
                                <InfoValue>{general.gestor || '-'}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Sector</InfoLabel>
                                <InfoValue>{general.sector || '-'}</InfoValue>
                            </InfoItem>
                        </InfoGrid>
                        {technologies.length > 0 && (
                            <div style={{ marginTop: '0.75rem' }}>
                                <InfoLabel>Tecnologias</InfoLabel>
                                <div style={{ marginTop: '4px' }}>
                                    {technologies.map((t, i) => (
                                        <TechBadge key={i}>{t}</TechBadge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Section>

                    {/* LLM Summary section */}
                    {(summary || summaryLoading) && (
                        <Section>
                            <SectionTitle>RESUMEN DE LA OFERTA</SectionTitle>
                            {summaryLoading ? (
                                <div style={{ color: '#666', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                    Cargando resumen...
                                </div>
                            ) : (
                                <div style={{ fontSize: '0.85rem', lineHeight: '1.6', color: '#333' }}>
                                    {summary!.split(/\n\n+/).map((para, idx) => (
                                        <p key={idx} style={{ margin: '0 0 0.75rem 0' }}>{para}</p>
                                    ))}
                                </div>
                            )}
                        </Section>
                    )}

                    {/* Oferta section */}
                    <Section>
                        <SectionTitle>📋 Oferta</SectionTitle>
                        <InfoGrid>
                            <InfoItem>
                                <InfoLabel>Estado</InfoLabel>
                                <InfoValue>
                                    {oferta.estado
                                        ? <StatusBadge status={oferta.estado}>{formatStatusLabel(oferta.estado)}</StatusBadge>
                                        : '-'}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Comercial</InfoLabel>
                                <InfoValue>{oferta.responsable || '-'}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Fecha inicio</InfoLabel>
                                <InfoValue>{formatDate(oferta.fecha_inicio)}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>Fecha entrega</InfoLabel>
                                <InfoValue>{formatDate(oferta.fecha_entrega)}</InfoValue>
                            </InfoItem>
                            {isAdmin && (
                                <>
                                    <InfoItem>
                                        <InfoLabel>Importe venta</InfoLabel>
                                        <InfoValue>{formatAmount(oferta.venta || oferta.amount)}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>Probabilidad</InfoLabel>
                                        <InfoValue>{oferta.probabilidad ? String(oferta.probabilidad).toUpperCase() : '-'}</InfoValue>
                                    </InfoItem>
                                </>
                            )}
                        </InfoGrid>
                        {oferta.equipo && oferta.equipo.length > 0 && (
                            <div style={{ marginTop: '0.75rem' }}>
                                <InfoLabel>Equipo Oferta ({oferta.equipo.length})</InfoLabel>
                                <TeamList style={{ marginTop: '4px' }}>
                                    {oferta.equipo.map((m: any, i: number) => (
                                        <TeamMemberRow key={i}>
                                            <span>{m.display_name || m.userName || `Usuario ${m.user_id || m.userId}`}</span>
                                            {isAdmin && <LoadBadge load={m.capacity_pct || m.load || 0}>{m.capacity_pct || m.load || 0}%</LoadBadge>}
                                        </TeamMemberRow>
                                    ))}
                                </TeamList>
                            </div>
                        )}
                    </Section>

                    {/* Proyecto section */}
                    {(proyecto.estado || proyecto.fecha_inicio || (proyecto.equipo && proyecto.equipo.length > 0)) && (
                        <Section>
                            <SectionTitle>🚀 Proyecto</SectionTitle>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>Estado</InfoLabel>
                                    <InfoValue>
                                        {proyecto.estado
                                            ? <StatusBadge status={proyecto.estado}>{formatStatusLabel(proyecto.estado)}</StatusBadge>
                                            : '-'}
                                    </InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Comercial</InfoLabel>
                                    <InfoValue>{proyecto.responsable || '-'}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Fecha inicio</InfoLabel>
                                    <InfoValue>{formatDate(proyecto.fecha_inicio)}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Fecha fin</InfoLabel>
                                    <InfoValue>{formatDate(proyecto.fecha_fin)}</InfoValue>
                                </InfoItem>
                            </InfoGrid>
                            {proyecto.comentarios && (
                                <div style={{ marginTop: '0.75rem' }}>
                                    <InfoLabel>Comentarios</InfoLabel>
                                    <InfoValue style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>{proyecto.comentarios}</InfoValue>
                                </div>
                            )}
                            {proyecto.equipo && proyecto.equipo.length > 0 && (
                                <div style={{ marginTop: '0.75rem' }}>
                                    <InfoLabel>Equipo Proyecto ({proyecto.equipo.length})</InfoLabel>
                                    <TeamList style={{ marginTop: '4px' }}>
                                        {proyecto.equipo.map((m: any, i: number) => (
                                            <TeamMemberRow key={i}>
                                                <span>{m.display_name || m.userName || `Usuario ${m.user_id || m.userId}`}</span>
                                                {isAdmin && <LoadBadge load={m.capacity_pct || m.load || 0}>{m.capacity_pct || m.load || 0}%</LoadBadge>}
                                            </TeamMemberRow>
                                        ))}
                                    </TeamList>
                                </div>
                            )}
                        </Section>
                    )}
                </ModalBody>

                <ModalFooter>
                    <FooterButton onClick={onClose}>Cerrar</FooterButton>
                    {isAdmin && <FooterButton primary onClick={handleViewDetails}>Ver detalles completos</FooterButton>}
                </ModalFooter>
            </Modal>
        </Overlay>
    )
}
